import React from 'react'

const sacredValley = {
    id: 'sacred-valley-ruins',
    type: 'post',
    countryId: 'peru',
    title: 'Sacred Valley: Ruins Beyond Machu Picchu',
    longDate: 'June 5, 2018',
    month: 'June',
    date: '5',
    coverImage: 'https://plivesey.github.io/world-of-wander-images/posts/peru/ruins/CoverRuinsSacredValley.jpg',
    content: [
        {
            text: (
                <div>We visited Peru with my (Alice’s) family in April 2018 – yes, that was technically one month before we quit our jobs and left on this adventure, but Peru was too cool not to share here. We only had one week to explore it and to get to and from <a href="/post/planning-machu-picchu">Machu Picchu</a>, but in three days we were able to see some other incredible ruins in Cusco city and the Sacred Valley. We’d love to go back and be able to explore some of the further out spots like <a href="https://www.lonelyplanet.com/peru/choquequirao">Choquequirao</a>, <a href="https://www.lonelyplanet.com/peru/attractions/chan-chan/a/poi-sig/1165434/1327130">Chan Chan</a>, and <a href="https://www.lonelyplanet.com/peru/kuelap">Kuelap</a> – one day!</div>
            )
        },
        {
            text: (
                <div>In Cusco you can buy a Boleta Turistica, a ticket that’s valid for 10 days and gets you into a dozen historical sites and ruins in Cusco and around the Sacred Valley. We bought ours at the entrance to Sacsayhuamán, and there are <a href="https://www.bestofperutravel.com/how-to-buy-the-boleto-turistico-or-cusco-tourist-ticket/">multiple other locations</a> in Cusco where you can get them.</div>
            )
        },
        {
            text: (
                <div>We highly recommend hiring a tour guide for Cusco and the Sacred Valley; we got so much more out of visiting the sites because of our guide Laura’s encyclopedic knowledge. You will also learn random facts about Quechua culture and the people of Cusco, like that people descended from native Quechuas process oxygen much more efficiently and have a higher red blood cell count, or that they love large, traditional lunches of <i>lomo saltado</i> (beef and fries) and eat small, snacky dinners.</div>
            )
        },
        {
            header: 'A Brief Historical Background'
        },
        {
            text: (
                <div>The first thing we learned about the Incas is that the term 'Inca civilization’ is a misnomer. The Incas were the rulers of the Quechua civilization, like the Pharaohs were to the Egyptians, and there were only 14 of them spanning a total of 94 years. The last Inca died as the Spanish were conquering Peru, and most of the technological and agricultural advances of the ancient Quechuas were lost.</div>
            )
        },
        {
            text: (
                <div>The Quechuas believed the world had three tiers: the sun (<i>Inti</i> &#47; god), the people, and mother earth (<i>Pacha Mama</i>, symbolized by a spiral). There are also animals that were important to them: the condor, the puma, and the snake. The Inca ruler, a demi-god, was the sun and his wife was the moon. We saw these symbols at several of the ruins we visited.</div>
            )
        },
        {
            text: (
                <div>The walls of the sites are truly incredible, and utilize an ancient, lost technology that makes them much stronger than many modern day buildings. In the Cusco earthquakes of 1650 and 1950, the ancient temples were the only structures to survive. The stone pieces are all unique sizes and shapes that are carved to fit together perfectly, but no one knows how they shaped them into these precise shapes. Some even have 10 or 11 sides! They don’t use any kind of cement, and are secured together like legos.</div>
            )
        },
        {
            text: (
                <div>After the Spanish arrived and the Quechuas were forced to build Catholic cathedrals and to provide statues and paintings, they left symbols from their culture in them: stars in a replica where the original had none; a snake woven into a fabric drape; and Mother Marys wearing sun halos with pregnant bellies (like Pacha Mama, symbolizing fertility.)</div>
            )
        },
        {
            text: (
                <div>I found the history of these sites and how little is known about their ancient religion, culture, and purpose to be fascinating, and the ruins were all gorgeous. We spent three days between Cusco and Ollantaytambo and were able to see several ruins along the way.</div>
            )
        },
        {
            header: 'Qorikancha'
        },
        {
            text: (
                <div>Qorikancha, the Sun Temple, is located right in the heart of Cusco city and was its religious center. The Spanish built Santo Domingo right on top of it, so only a few remains are available to see today. At its height, there would have been gold pieces and plated plaques (the "sun") but all gold was stripped from these sites.</div>
            )
        },
        {
            image: 'https://plivesey.github.io/world-of-wander-images/posts/peru/ruins/QorikanchaWalls.jpg',
            detailText: 'The mysterious and extremely sturdy walls at Qorikancha.'
        },
        {
            image: 'https://plivesey.github.io/world-of-wander-images/posts/peru/ruins/QorikanchaWindows.jpg',
            detailText: 'Identical trapezoidal windows.'
        },
        {
            image: 'https://plivesey.github.io/world-of-wander-images/posts/peru/ruins/QorikanchaGold.jpg',
            detailText: 'A replica of a long gone gold plaque depicting humankind’s relationship to the gods and the earth.'
        },
        {
            header: 'Sacsayhuamán'
        },
        {
            text: (
                <div>There’s a really easy way to remember how to pronounce this place: sex-y wo-man (say it with an accent, rounding out your vowels.)</div>
            )
        },
        {
            text: (
                <div>At this fortified complex on a hilltop overlooking Cusco, not much remains except the large terraced walls. Archaeologists think it may have been built by the Killke culture preceding the Incas, and used as a citadel or fortress. Most of what the Spaniards knew about the place came from the chronicles of <a href="https://en.wikipedia.org/wiki/Inca_Garcilaso_de_la_Vega">Garcilaso de la Vega</a>, who only saw the site when he was six years old. According to our guide, a lot of what we know about the Incas and Quechuas came from him, but since he left Peru at a young age it’s impossible to know how reliable his account is.</div>
            )
        },
        {
            image: 'https://plivesey.github.io/world-of-wander-images/posts/peru/ruins/Sacsayhuaman1.jpg'
        },
        {
            header: 'Qenko'
        },
        {
            text: (
                <div>Located just outside Cusco near Sacsayhuamán, Qenko is a holy site based on a natural rock formation. Our guide pointed to a few rock formations that are supposed to look like a condor and a snake, though it was a little difficult to make them out. 'Qenko’ means zigzag, and the walls that were built around the natural rock formations have zigzags hewn in. In one part of the site, you can walk through a semi-underground tunnel where it is believed sacrifices and mummifications took place.</div>
            )
        },
        {
            image: 'https://plivesey.github.io/world-of-wander-images/posts/peru/ruins/Qenko.jpg',
            detailText: 'Alice’s mom, Judy, going underground at Qenko.'
        },
        {
            image: 'https://plivesey.github.io/world-of-wander-images/posts/peru/ruins/QenkoView.jpg',
            detailText: 'A view of the colorful Cusco city from the rocky outcrop.'
        },
        {
            header: 'Tambomachay'
        },
        {
            text: (
                <div>Known as the Inca baths, Tambomachay is a series of structures that house aqueducts spouting water into a small pool. The source of the water is unknown (well, really it’s underground, but 'unknown’ is more mystical) and since the flow never dries up, it is said to have special powers. Quechuas believed that bathing in it would purify you, and drinking it would bring you fertility. This small site was nearly empty of tourists and extremely peaceful when we visited.</div>
            )
        },
        {
            image: 'https://plivesey.github.io/world-of-wander-images/posts/peru/ruins/Tambomachay.jpg'
        },
        {
            header: 'Pisac'
        },
        {
            text: (
                <div>The bottom part of the Pisac ruins is all terraces, built to stand the test of time and landslides. None of the archaeological sites in Peru were protected until 1968 (when Machu Picchu became a big thing), so it is only recently that they have done restoration work to repair the terraces damaged by erosion. The site, which is located atop a hill overlooking present-day Pisac and surrounded by gorgeous scenery, served at least three purposes: military, religious, and agricultural. </div>
            )
        },
        {
            image: 'https://plivesey.github.io/world-of-wander-images/posts/peru/ruins/Pisac2.jpg',
            detailText: 'The Pisac ruins are set against a stunning backdrop of mountains.'
        },
        {
            image: 'https://plivesey.github.io/world-of-wander-images/posts/peru/ruins/Pisac1.jpg'
        },
        {
            text: (
                <div>Catacombs pepper the hillside opposite the main site – barely visible small holes surrounded by succulents.</div>
            )
        },
        {
            image: 'https://plivesey.github.io/world-of-wander-images/posts/peru/ruins/Pisac3.jpg'
        },
        {
            header: 'Moray'
        },
        {
            text: (
                <div>Outside of Machu Picchu and Ollantaytambo, Moray was our favorite stop of the trip. There were very few visitors when we went, and its not included on most Sacred Valley tours; we had to stress that we wanted to go here and not spend more time buying touristy tchotchkes at the markets.</div>
            )
        },
        {
            text: (
                <div>For a long time, historians thought Moray was a temple dedicated to Pacha Mama because of its concentric circles that slope downward to the bottom of the crater. But during restoration, they discovered seeds of bizarre fruits and vegetables like avocados, which normally can’t be grown so high in the Andes. They now think it was a testing ground for domesticating seeds to grow in varied climates. There is a whopping 7-degree temperature difference between the bottom and upper terraces, essentially creating a false jungle in the mountains. They also now think the massive divot in the earth, as well as the two smaller divots nearby, were caused by meteorites, which are common in the region.</div>
            )
        },
        {
            image: 'https://plivesey.github.io/world-of-wander-images/posts/peru/ruins/Moray.jpg',
            detailText: 'Concentric circles of terraces at Moray.'
        },
        {
            image: 'https://plivesey.github.io/world-of-wander-images/posts/peru/ruins/Moray2.jpg',
            detailText: 'A second, smaller site with an amazing backdrop.'
        },
        {
            header: 'Ollantaytambo'
        },
        {
            text: (
                <div>The town of Ollantaytambo is the departure point for Machu Picchu, either by train or multi-day backpacking trek. I fell in love with this tiny, quaint town and the beautiful ruins that dominate it. The most adorable covered bridge runs over a river just down the street from the main town square, and is magical when the mist rolls off the mountains.</div>
            )
        },
        {
            text: (
                <div>On the day we drove through the Sacred Valley, stopping at Pisac and Moray as well as a llama farm, textile producer, and local market, we ended in Ollantaytambo at dinnertime. Instead of rushing to Machu Picchu the next morning we opted for a full day there to acclimatize more and relax, and it was very much worth it. There are several <a href="https://moon.com/2014/02/three-great-day-hikes-in-ollantaytambo-peru/">day hikes</a> you can do from the town, but since they’re 6-8 hours, we decided not to do them. Next time!</div>
            )
        },
        {
            text: (
                <div>Ollantaytambo is an intimidating fortress made of tall walls and terraces. During the Spanish Conquest, it was the last line of defense for the Quechuas. Manco Inca Yupanqui led the Quechua forces at the Battle of Ollantaytambo, where they successfully held off the Spanish and forced them to retreat to Cusco. Manco Inca then withdrew his forces far into the jungle at Vilcabamba, where the resistance lasted as the Neo-Inca State for forty more years.</div>
            )
        },
        {
            image: 'https://plivesey.github.io/world-of-wander-images/posts/peru/ruins/Ollantaytambo.jpg'
        },
        {
            header: 'Pinkuylluna'
        },
        {
            text: (
                <div>The remains of a granary on the hill opposite Ollantaytambo give a stunning view of the ruins, and are beautiful (and free!) themselves. After a couple of hours exploring Ollantaytambo and resting for lunch, I hiked the short but steep hill up to it. You enter through the pedestrian-only cobbled streets of the town, which are also beautiful to explore. However, I’m not sure why the Quechuas decided that halfway up a massive hill was the best place to store bags of grain, especially since they presumably needed to be walked up and down by humans since there are several staircases on the climb.</div>
            )
        },
        {
            image: 'https://plivesey.github.io/world-of-wander-images/posts/peru/ruins/Pinkuylluna1.jpg',
        },
        {
            image: 'https://plivesey.github.io/world-of-wander-images/posts/peru/ruins/Pinkuylluna2.jpg',
        }
    ]
}


export default sacredValley


// WEBPACK FOOTER //
// ./src/Posts/Content/SacredValley.js